import { INVOICE_STATUS, PAYMENT_METHOD } from '@monorepo/shared/schemas/common'
import type {
  Invoice,
  InvoiceQuery,
  InvoiceStatus,
} from '@monorepo/shared/schemas/invoices'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Edit,
  Eye,
  FileCheck,
  FileText,
  Home,
  Mail,
  MoreHorizontal,
  Receipt,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import {
  CreateInvoiceDialog,
  formatCentsAsCurrency,
  InvoiceFilters,
  type InvoiceFiltersType,
  invoicesService,
  paymentsService,
  RecordPaymentDialog,
  SendInvoiceEmailDialog,
  UpdateInvoiceStatusDialog,
} from '@/features/invoices'
import { toast } from '@/lib/toast'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Badge } from '@/shared/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'

// Extended invoice type with customer info from API response
type InvoiceWithCustomer = Invoice & {
  customer?: {
    id: string
    name: string
    email?: string | null
  }
}

export const Route = createFileRoute('/_auth/invoices')({
  component: InvoicesPage,
})

function InvoicesPage() {
  const navigate = useNavigate()
  const { currentEnvironment } = useEnvironment()
  const { setBreadcrumbs } = useBreadcrumbs()
  const [invoices, setInvoices] = useState<InvoiceWithCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<InvoiceFiltersType>({})

  // Dialog states for actions
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false)
  const [showUpdateStatusDialog, setShowUpdateStatusDialog] = useState(false)
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [invoiceForAction, setInvoiceForAction] =
    useState<InvoiceWithCustomer | null>(null)

  // Breadcrumbs
  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Invoices</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  const loadInvoices = useCallback(async () => {
    setLoading(true)
    try {
      const query: InvoiceQuery = {}
      // Wire invoice_status filter (send first selected value)
      if (
        filters.invoice_status &&
        Array.isArray(filters.invoice_status) &&
        filters.invoice_status.length > 0
      ) {
        query.status = filters.invoice_status[0] as InvoiceStatus
      }
      // Wire payment_status filter
      if (
        filters.payment_status &&
        Array.isArray(filters.payment_status) &&
        filters.payment_status.length > 0
      ) {
        query.payment_status = filters.payment_status[0]
      }
      // Wire field-based text filter
      if (filters.field && filters.operator && filters.value) {
        query.filter_field = filters.field as InvoiceQuery['filter_field']
        query.filter_operator =
          filters.operator as InvoiceQuery['filter_operator']
        query.filter_value = filters.value
      }

      const response = await invoicesService.getInvoices(query)
      setInvoices(response.data)
    } catch {
      // Silently handled - loading state will clear
    } finally {
      setLoading(false)
    }
  }, [filters, currentEnvironment?.id])

  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  const getStatusVariant = (status: InvoiceStatus) => {
    switch (status) {
      case INVOICE_STATUS.approved:
      case INVOICE_STATUS.sent:
        return 'secondary'
      case INVOICE_STATUS.draft:
      case INVOICE_STATUS.pending_approval:
        return 'outline'
      case INVOICE_STATUS.void:
      case INVOICE_STATUS.uncollectible:
      case INVOICE_STATUS.disputed:
        return 'destructive'
      case INVOICE_STATUS.viewed:
        return 'default'
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: InvoiceStatus) => {
    switch (status) {
      case INVOICE_STATUS.approved:
      case INVOICE_STATUS.sent:
      case INVOICE_STATUS.viewed:
        return <CheckCircle className='w-4 h-4' />
      case INVOICE_STATUS.pending_approval:
        return <Clock className='w-4 h-4' />
      case INVOICE_STATUS.draft:
        return <FileText className='w-4 h-4' />
      case INVOICE_STATUS.void:
      case INVOICE_STATUS.uncollectible:
      case INVOICE_STATUS.disputed:
        return <XCircle className='w-4 h-4' />
      default:
        return <AlertCircle className='w-4 h-4' />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleViewInvoiceDetails = (invoice: InvoiceWithCustomer) => {
    navigate({
      to: '/invoice-detail/$invoiceId',
      params: { invoiceId: invoice.id },
      search: { from: 'invoices' },
    })
  }

  const handleFiltersChange = (newFilters: InvoiceFiltersType) => {
    setFilters(newFilters)
  }

  // Dialog handlers
  const handleRecordPayment = (
    invoice: InvoiceWithCustomer,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    setInvoiceForAction(invoice)
    setShowRecordPaymentDialog(true)
  }

  const handleUpdateStatus = (
    invoice: InvoiceWithCustomer,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    setInvoiceForAction(invoice)
    setShowUpdateStatusDialog(true)
  }

  const _handleSendEmail = (
    invoice: InvoiceWithCustomer,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    setInvoiceForAction(invoice)
    setShowEmailDialog(true)
  }

  // Action handlers
  const handleFinalize = async (
    invoice: InvoiceWithCustomer,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    if (
      !confirm(
        `Finalize invoice ${invoice.invoice_number}? This cannot be undone.`
      )
    ) {
      return
    }
    try {
      await invoicesService.finalizeInvoice(invoice.id)
      toast.success('Invoice finalized successfully')
      loadInvoices()
    } catch {
      toast.error('Failed to finalize invoice')
    }
  }

  const handleVoid = async (
    invoice: InvoiceWithCustomer,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    const reason = prompt('Reason for voiding this invoice:')
    if (!reason) {
      return
    }
    try {
      await invoicesService.voidInvoice(invoice.id, { reason })
      toast.success('Invoice voided successfully')
      loadInvoices()
    } catch {
      toast.error('Failed to void invoice')
    }
  }

  const handleMarkAsPaid = async (
    invoice: InvoiceWithCustomer,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation()
    if (!confirm(`Mark invoice ${invoice.invoice_number} as fully paid?`)) {
      return
    }
    try {
      await paymentsService.recordPayment({
        invoice_id: invoice.id,
        amount: invoice.amount_due_cents / 100,
        currency: invoice.currency,
        payment_method: PAYMENT_METHOD.other,
        notes: 'Marked as paid',
      })
      toast.success('Invoice marked as paid')
      loadInvoices()
    } catch {
      toast.error('Failed to mark invoice as paid')
    }
  }

  const handleEmailSubmit = (_data: {
    recipient: string
    subject: string
    message: string
  }) => {
    // TODO: Send email via API
    setInvoiceForAction(null)
  }

  const handleSearch = () => {
    loadInvoices()
  }

  const handleReset = () => {
    setFilters({})
  }

  const handleInvoiceCreated = () => {
    loadInvoices()
  }

  // Define table columns
  const invoiceColumns: ColumnDef<InvoiceWithCustomer>[] = [
    {
      accessorKey: 'invoice_number',
      header: 'Invoice ID',
      size: 250,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.invoice_number}</div>
          <div className='text-sm text-muted-foreground'>{row.original.id}</div>
        </div>
      ),
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      size: 400,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {row.original.customer?.name || 'Unknown'}
          </div>
          <div className='text-sm text-muted-foreground'>
            {row.original.customer?.email || 'No email'}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'total_cents',
      header: 'Amount',
      size: 200,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {formatCentsAsCurrency(
              row.original.total_cents,
              row.original.currency
            )}
          </div>
          {row.original.tax_cents > 0 && (
            <div className='text-sm text-muted-foreground'>
              +
              {formatCentsAsCurrency(
                row.original.tax_cents,
                row.original.currency
              )}{' '}
              tax
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 150,
      cell: ({ row }) => {
        const isOverdue =
          row.original.payment_status === 'overdue' ||
          (row.original.status === INVOICE_STATUS.sent &&
            row.original.due_date &&
            new Date(row.original.due_date) < new Date())
        return (
          <div className='flex flex-col gap-1'>
            <Badge
              variant={getStatusVariant(row.original.status)}
              className='gap-1 w-fit'
            >
              {getStatusIcon(row.original.status)}
              {row.original.status.charAt(0).toUpperCase() +
                row.original.status.slice(1).replace('_', ' ')}
            </Badge>
            {isOverdue && (
              <Badge variant='destructive' className='text-xs w-fit'>
                Overdue
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'payment_status',
      header: 'Payment Status',
      size: 150,
      cell: ({ row }) => {
        const paymentStatus = row.original.payment_status
        if (paymentStatus === 'paid') {
          return (
            <div className='flex items-center gap-1 text-green-600'>
              <CheckCircle className='w-4 h-4' />
              <span className='text-sm'>Paid</span>
            </div>
          )
        }
        if (paymentStatus === 'partially_paid') {
          return (
            <div className='flex items-center gap-1 text-blue-600'>
              <Clock className='w-4 h-4' />
              <span className='text-sm'>Partial</span>
            </div>
          )
        }
        if (paymentStatus === 'overdue') {
          return (
            <div className='flex items-center gap-1 text-red-600'>
              <AlertCircle className='w-4 h-4' />
              <span className='text-sm'>Overdue</span>
            </div>
          )
        }
        if (paymentStatus === 'refunded') {
          return (
            <div className='flex items-center gap-1 text-purple-600'>
              <RefreshCw className='w-4 h-4' />
              <span className='text-sm'>Refunded</span>
            </div>
          )
        }
        return (
          <div className='flex items-center gap-1 text-muted-foreground'>
            <FileText className='w-4 h-4' />
            <span className='text-sm'>Unpaid</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'issue_date',
      header: 'Issued',
      size: 150,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Calendar className='w-4 h-4 text-muted-foreground' />
          <span className='text-sm'>{formatDate(row.original.issue_date)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'due_date',
      header: 'Due Date',
      size: 150,
      cell: ({ row }) => {
        if (!row.original.due_date) {
          return <span className='text-sm text-muted-foreground'>-</span>
        }
        const isOverdue =
          row.original.payment_status !== 'paid' &&
          new Date(row.original.due_date) < new Date()
        return (
          <div
            className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}
          >
            <Calendar className='w-4 h-4 text-muted-foreground' />
            <span className='text-sm'>{formatDate(row.original.due_date)}</span>
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      size: 90,
      cell: ({ row }) => {
        const inv = row.original
        const isDraft = inv.status === INVOICE_STATUS.draft
        const isVoid = inv.status === INVOICE_STATUS.void
        const isPaid = inv.payment_status === 'paid'

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem onClick={() => handleViewInvoiceDetails(inv)}>
                <Eye className='w-4 h-4 mr-2' />
                View Details
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {isDraft && (
                <DropdownMenuItem onClick={e => handleFinalize(inv, e)}>
                  <FileCheck className='w-4 h-4 mr-2' />
                  Finalize
                </DropdownMenuItem>
              )}

              {!isPaid && !isVoid && (
                <>
                  <DropdownMenuItem onClick={e => handleRecordPayment(inv, e)}>
                    <CreditCard className='w-4 h-4 mr-2' />
                    Record Payment
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={e => handleMarkAsPaid(inv, e)}>
                    <CheckCircle className='w-4 h-4 mr-2' />
                    Mark as Paid
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuItem onClick={e => handleUpdateStatus(inv, e)}>
                <Edit className='w-4 h-4 mr-2' />
                Update Status
              </DropdownMenuItem>

              {!isVoid && (
                <DropdownMenuItem
                  onClick={e => handleVoid(inv, e)}
                  className='text-destructive'
                >
                  <XCircle className='w-4 h-4 mr-2' />
                  Void
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Future scope — kept as no-op */}
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  toast.info('Coming soon')
                }}
              >
                <Mail className='w-4 h-4 mr-2' />
                Send Email
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  toast.info('Coming soon')
                }}
              >
                <Send className='w-4 h-4 mr-2' />
                Resend Invoice
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  toast.info('Coming soon')
                }}
              >
                <Receipt className='w-4 h-4 mr-2' />
                Issue Credit Note
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  if (loading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='space-y-6'>
          <div className='flex justify-between items-center'>
            <div>
              <h1 className='text-3xl font-bold'>Invoices</h1>
              <p className='text-muted-foreground'>
                View and manage all customer invoices
              </p>
            </div>
            <CreateInvoiceDialog onInvoiceCreated={handleInvoiceCreated} />
          </div>

          <InvoiceFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSearch={handleSearch}
            onReset={handleReset}
          />

          <div className='bg-transparent border rounded-lg'>
            <div className='animate-pulse space-y-4 p-6'>
              <div className='grid grid-cols-9 gap-4 pb-4 border-b'>
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={`header-${i}`}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  />
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <div key={`row-${i}`} className='grid grid-cols-9 gap-4 py-3'>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={`cell-${i}-${j}`}
                      className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex justify-between items-center'>
          <div>
            <h1 className='text-3xl font-bold'>Invoices</h1>
            <p className='text-muted-foreground'>
              View and manage all customer invoices
            </p>
          </div>
          <CreateInvoiceDialog onInvoiceCreated={handleInvoiceCreated} />
        </div>

        {/* Filters */}
        <InvoiceFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          onReset={handleReset}
        />

        {/* Invoices Table */}
        <div className='bg-transparent border rounded-lg'>
          {invoices.length > 0 ? (
            <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50 [&_tbody_tr]:cursor-pointer'>
              <VirtualizedTable
                data={invoices}
                columns={invoiceColumns.map(col => ({
                  ...col,
                  enableSorting: false,
                }))}
                containerHeight='calc(100vh - 380px)'
                className='bg-transparent'
                onRowClick={row => handleViewInvoiceDetails(row)}
                emptyMessage='No invoices found. Try adjusting your filters or create your first invoice.'
                loadingMessage='Loading more invoices...'
                enableSorting={true}
                enableColumnResizing={true}
                enableColumnReordering={true}
                enableColumnPinning={false}
              />
            </div>
          ) : (
            <div className='text-center py-12 text-muted-foreground'>
              <FileText className='w-12 h-12 mx-auto mb-4 opacity-50' />
              <h3 className='text-lg font-medium mb-2'>No invoices found</h3>
              <p className='text-sm max-w-sm mx-auto mb-4'>
                No invoices have been created yet. Invoices will appear here
                once they are generated.
              </p>
            </div>
          )}
        </div>

        {/* Shared Invoice Action Dialogs */}
        <RecordPaymentDialog
          invoice={invoiceForAction as unknown as Invoice | null}
          open={showRecordPaymentDialog}
          onOpenChange={setShowRecordPaymentDialog}
          onSuccess={loadInvoices}
        />

        <UpdateInvoiceStatusDialog
          invoice={invoiceForAction as unknown as Invoice | null}
          open={showUpdateStatusDialog}
          onOpenChange={setShowUpdateStatusDialog}
          onSuccess={loadInvoices}
        />

        <SendInvoiceEmailDialog
          invoice={invoiceForAction as never}
          open={showEmailDialog}
          onOpenChange={setShowEmailDialog}
          onSubmit={handleEmailSubmit}
        />
      </div>
    </div>
  )
}
